import posthog from 'posthog-js';

// Flag to track if PostHog has been initialized
let isInitialized = false;

/**
 * Initialize PostHog with the provided API key and options
 * This ensures PostHog is only initialized once
 */
export const initializePostHog = (): void => {
  // Skip if already initialized or if API key is not provided
  if (isInitialized || !import.meta.env.VITE_POSTHOG_API_KEY) {
    return;
  }

  try {
    console.log('Initializing PostHog');
    posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
      api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: false,
      // Disable in development mode
      opt_out_capturing_by_default: import.meta.env.DEV,
    });

    isInitialized = true;
    console.log('PostHog initialized successfully');
  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
  }
};

/**
 * Track an event with PostHog
 * @param eventName The name of the event to track
 * @param properties Optional properties to include with the event
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>): void => {
  if (isInitialized) {
    posthog.capture(eventName, properties);
  }
};

/**
 * Identify a user with PostHog
 * @param userId The ID of the user to identify
 * @param properties Optional properties to include with the user
 */
export const identifyUser = (userId: string, properties?: Record<string, any>): void => {
  if (isInitialized) {
    posthog.identify(userId, properties);
  }
};

/**
 * Reset the current user in PostHog
 */
export const resetUser = (): void => {
  if (isInitialized) {
    posthog.reset();
  }
};

/**
 * Opt in to PostHog tracking
 */
export const optIn = (): void => {
  if (isInitialized) {
    posthog.opt_in_capturing();
  }
};

/**
 * Opt out of PostHog tracking
 */
export const optOut = (): void => {
  if (isInitialized) {
    posthog.opt_out_capturing();
  }
};

// Export the PostHog instance for direct access if needed
export { posthog };
